/**
 * Batch manager for event transmission
 * Handles batching, transmission, retry logic, and offline support
 * 
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 14.3
 */

import { EventQueue } from './EventQueue';
import { TrackingEvent } from '../types/events';

/**
 * Configuration for the batch manager
 */
export interface BatchManagerConfig {
  /** Number of events to batch before transmission */
  batchSize: number;
  /** Flush interval in milliseconds */
  flushInterval: number;
  /** API endpoint URL */
  apiBaseUrl: string;
  /** JWT token string or async function that returns a fresh token */
  authToken: string | (() => Promise<string>);
}

/**
 * Result of a batch transmission attempt
 */
interface TransmitResult {
  success: boolean;
  statusCode?: number;
  retryAfter?: number; // Milliseconds to wait before retry
  error?: string;
}

/**
 * BatchManager handles event batching and transmission to the backend API
 * 
 * Features:
 * - Automatic batching when batch size is reached
 * - Periodic flushing based on flush interval
 * - Exponential backoff retry logic for failed transmissions
 * - HTTP status code handling (202, 429, 400, 500)
 * - Page unload handler using navigator.sendBeacon()
 * - Network error handling with retry
 */
export class BatchManager {
  private queue: EventQueue;
  private config: BatchManagerConfig;
  private flushTimer: NodeJS.Timeout | number | null = null;
  private pollTimer: NodeJS.Timeout | number | null = null;
  private isTransmitting = false;
  private retryDelays = [100, 200, 400, 800, 1600]; // Exponential backoff in ms
  private maxRetryDelay = 300000; // 5 minutes max retry delay

  /**
   * Create a new BatchManager instance
   * 
   * @param queue - EventQueue instance for storing events
   * @param config - Configuration for batch transmission
   */
  constructor(queue: EventQueue, config: BatchManagerConfig) {
    this.queue = queue;
    this.config = config;
  }

  /**
   * Start the batch manager
   * - Begins polling the queue for batch size check
   * - Sets up flush interval timer
   * - Attaches page unload handler
   * 
   * Requirement 3.1: Batch size monitoring
   * Requirement 3.2: Flush interval timer
   * Requirement 3.7: Page unload handler
   */
  start(): void {
    // Poll queue every 5 seconds to check size
    this.pollTimer = setInterval(() => {
      this.checkBatchSize();
    }, 5000);

    // Schedule periodic flush
    this.scheduleFlush();

    // Attach page unload handler
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.handlePageUnload);
      // Also listen for pagehide for better mobile support
      window.addEventListener('pagehide', this.handlePageUnload);
    }
  }

  /**
   * Stop the batch manager
   * - Clears timers
   * - Removes event listeners
   */
  stop(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer as number);
      this.pollTimer = null;
    }

    if (this.flushTimer) {
      clearTimeout(this.flushTimer as number);
      this.flushTimer = null;
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.handlePageUnload);
      window.removeEventListener('pagehide', this.handlePageUnload);
    }
  }

  /**
   * Manually flush all queued events immediately
   * 
   * @returns Promise that resolves when flush completes
   */
  async flushNow(): Promise<void> {
    // Cancel scheduled flush
    if (this.flushTimer) {
      clearTimeout(this.flushTimer as number);
      this.flushTimer = null;
    }

    await this.transmitBatch();

    // Reschedule next flush
    this.scheduleFlush();
  }

  /**
   * Schedule the next periodic flush
   * 
   * Requirement 3.2: Flush interval timer
   */
  private scheduleFlush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer as number);
    }

    this.flushTimer = setTimeout(() => {
      this.transmitBatch().then(() => {
        // Schedule next flush
        this.scheduleFlush();
      });
    }, this.config.flushInterval);
  }

  /**
   * Check if batch size threshold is reached and transmit if needed
   * 
   * Requirement 3.1: Batch size monitoring
   */
  private async checkBatchSize(): Promise<void> {
    try {
      const size = await this.queue.size();
      if (size >= this.config.batchSize && !this.isTransmitting) {
        await this.transmitBatch();
      }
    } catch (error) {
      console.warn('[BatchManager] Error checking batch size:', error);
    }
  }

  /**
   * Transmit a batch of events to the backend
   * 
   * Handles:
   * - Fetching events from queue
   * - HTTP transmission with JWT token
   * - Retry logic with exponential backoff
   * - Status code handling (202, 429, 400, 500)
   * - Removing successfully transmitted events
   * 
   * Requirements: 3.3, 3.4, 3.5, 3.6, 14.3
   */
  private async transmitBatch(): Promise<void> {
    if (this.isTransmitting) {
      return; // Prevent concurrent transmissions
    }

    this.isTransmitting = true;

    try {
      // Get batch of events from queue
      const size = await this.queue.size();
      if (size === 0) {
        return;
      }

      const batchSize = Math.min(size, this.config.batchSize);
      const events = await this.queue.peek(batchSize);

      if (events.length === 0) {
        return;
      }

      // Attempt transmission with retry logic
      const result = await this.transmitWithRetry(events);

      if (result.success) {
        // Requirement 3.3: Remove transmitted events on success
        await this.queue.dequeue(events.length);
        console.log(`[BatchManager] Successfully transmitted ${events.length} events`);
      } else if (result.statusCode === 400) {
        // Requirement 3.6: Discard events on client error (malformed data)
        await this.queue.dequeue(events.length);
        console.error(`[BatchManager] Discarding ${events.length} events due to client error:`, result.error);
      } else if (result.statusCode === 429 || result.statusCode === 500) {
        // Requirement 3.4 & 3.5: Retry after delay for rate limit or server error
        // Events remain in queue for next attempt
        console.warn(`[BatchManager] Transmission failed with status ${result.statusCode}, will retry later`);
      } else {
        // Network error or other error - events remain in queue
        // Requirement 14.3: Network error retry with exponential backoff
        console.warn(`[BatchManager] Transmission failed, will retry later:`, result.error);
      }
    } catch (error) {
      console.error('[BatchManager] Error during batch transmission:', error);
    } finally {
      this.isTransmitting = false;
    }
  }

  /**
   * Transmit events with exponential backoff retry logic
   * 
   * Requirement 3.4: Retry with Retry-After header for 429
   * Requirement 3.5: Exponential backoff for 500 errors
   * Requirement 14.3: Network error retry with exponential backoff
   * 
   * @param events - Events to transmit
   * @returns Result of transmission attempt
   */
  private async transmitWithRetry(events: TrackingEvent[]): Promise<TransmitResult> {
    let lastResult: TransmitResult = { success: false };

    for (let attempt = 0; attempt < this.retryDelays.length; attempt++) {
      // Attempt transmission
      lastResult = await this.sendBatch(events);

      if (lastResult.success) {
        return lastResult;
      }

      // Don't retry on client errors (400)
      if (lastResult.statusCode === 400) {
        return lastResult;
      }

      // For last attempt, don't wait
      if (attempt === this.retryDelays.length - 1) {
        break;
      }

      // Calculate retry delay
      let retryDelay: number;
      if (lastResult.statusCode === 429 && lastResult.retryAfter) {
        // Requirement 3.4: Use Retry-After header for 429 responses
        retryDelay = lastResult.retryAfter;
      } else {
        // Exponential backoff for 500 errors and network errors
        retryDelay = this.retryDelays[attempt];
      }

      // Cap retry delay at max
      retryDelay = Math.min(retryDelay, this.maxRetryDelay);

      console.log(`[BatchManager] Retrying in ${retryDelay}ms (attempt ${attempt + 1}/${this.retryDelays.length})`);

      // Wait before retry
      await this.sleep(retryDelay);
    }

    return lastResult;
  }

  /**
   * Send a batch of events via fetch API
   * 
   * Handles:
   * - HTTP POST with JWT authentication
   * - Response status code parsing
   * - Retry-After header extraction
   * - Network error handling
   * 
   * Requirements: 3.3, 3.4, 3.5, 3.6
   * 
   * @param events - Events to send
   * @returns Result of transmission
   */
  private async sendBatch(events: TrackingEvent[]): Promise<TransmitResult> {
    try {
      const endpoint = `${this.config.apiBaseUrl}/activity-tracking/v1/events`;

      // Resolve token fresh on every send - handles expiry automatically
      const token = typeof this.config.authToken === 'function'
        ? await this.config.authToken()
        : this.config.authToken;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token || '',
        },
        body: JSON.stringify({ events }),
      });

      // Requirement 3.3: HTTP 202 indicates success
      if (response.status === 202) {
        return { success: true, statusCode: 202 };
      }

      // Requirement 3.4: HTTP 429 indicates rate limiting
      if (response.status === 429) {
        const retryAfter = this.parseRetryAfter(response.headers.get('Retry-After'));
        return {
          success: false,
          statusCode: 429,
          retryAfter,
          error: 'Rate limited',
        };
      }

      // Requirement 3.6: HTTP 400 indicates client error
      if (response.status === 400) {
        const errorText = await response.text().catch(() => 'Unknown error');
        return {
          success: false,
          statusCode: 400,
          error: errorText,
        };
      }

      // Requirement 3.5: HTTP 500 indicates server error
      if (response.status >= 500) {
        const errorText = await response.text().catch(() => 'Server error');
        return {
          success: false,
          statusCode: response.status,
          error: errorText,
        };
      }

      // Other error status codes
      return {
        success: false,
        statusCode: response.status,
        error: `HTTP ${response.status}`,
      };
    } catch (error) {
      // Network error (no connection, timeout, etc.)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Parse Retry-After header value
   * Supports both delay-seconds and HTTP-date formats
   * 
   * @param retryAfterHeader - Retry-After header value
   * @returns Retry delay in milliseconds
   */
  private parseRetryAfter(retryAfterHeader: string | null): number {
    if (!retryAfterHeader) {
      return 60000; // Default 60 seconds
    }

    // Try parsing as seconds
    const seconds = parseInt(retryAfterHeader, 10);
    if (!isNaN(seconds)) {
      return seconds * 1000;
    }

    // Try parsing as HTTP date
    const date = new Date(retryAfterHeader);
    if (!isNaN(date.getTime())) {
      const delay = date.getTime() - Date.now();
      return Math.max(0, delay);
    }

    // Default fallback
    return 60000;
  }

  /**
   * Handle page unload event
   * Uses navigator.sendBeacon() for reliable event transmission during page unload
   * 
   * Requirement 3.7: Page unload handler with sendBeacon
   */
  private handlePageUnload = async (): Promise<void> => {
    try {
      // Get all remaining events
      const size = await this.queue.size();
      if (size === 0) {
        return;
      }

      const events = await this.queue.peek(size);
      if (events.length === 0) {
        return;
      }

      // Use sendBeacon for reliable transmission during unload
      if (navigator.sendBeacon) {
        const endpoint = `${this.config.apiBaseUrl}/activity-tracking/v1/events`;
        const blob = new Blob(
          [JSON.stringify({ events })],
          { type: 'application/json' }
        );

        // sendBeacon doesn't support custom headers, so we append token as query param
        // This is acceptable for unload scenarios
        const token = typeof this.config.authToken === 'function'
          ? await this.config.authToken().catch(() => '')
          : this.config.authToken;
        const url = `${endpoint}?token=${token}`;
        
        const success = navigator.sendBeacon(url, blob);
        
        if (success) {
          // Clear the queue since sendBeacon was successful
          await this.queue.clear();
          console.log(`[BatchManager] Sent ${events.length} events via sendBeacon`);
        }
      } else {
        // Fallback: try synchronous fetch (may not complete)
        console.warn('[BatchManager] sendBeacon not available, using fallback');
      }
    } catch (error) {
      console.error('[BatchManager] Error in page unload handler:', error);
    }
  };

  /**
   * Sleep utility for retry delays
   * 
   * @param ms - Milliseconds to sleep
   * @returns Promise that resolves after delay
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
